import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  ArrowLeft, Flame, Loader2, Plus, Upload, Gauge, CalendarRange,
  CheckCircle2, AlertTriangle, Settings, X, FileText, Download, FileSpreadsheet,
  Mail, Copy, Send,
} from "lucide-react";
import { useEffect, useRef, useState } from "react";
import {
  Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis,
} from "recharts";
import { api, baixarArquivoPost } from "../lib/api";
import { usePermissoes } from "../lib/permissoes";
import { CHART_AXIS, CHART_GRID, CHART_TOOLTIP, GLASS_CHART_CARD, PIE_COLORS_DARK } from "../lib/chartTheme";

interface ConsumoMensal {
  unidades: string[];
  periodos: { mes: string; label: string; ini: string; fim: string; total_aptos: number; operadora: number | null; aptos: Record<string, number> }[];
}

interface Props {
  condominioId: number;
  onVoltar: () => void;
  /** Abre direto na aba Ciclos & Rateio, destacando este mês ("AAAA-MM"). */
  mesReferencia?: string;
  /** Rótulo do botão de retorno (ex.: quando veio do Resumo de DOC's). */
  voltarLabel?: string;
}

// Histórico do caderno do zelador do Golden Garden (jan–set/2026), formato
// data,unidade,anotado. Serve como preenchimento rápido para o 1º cadastro;
// o síndico pode editar antes de importar.
const SEED_GOLDEN = `2026-01-16,602,5940584
2026-01-16,601,3008148
2026-01-16,502,2034656
2026-01-16,501,2697341
2026-01-16,402,1193732
2026-01-16,401,3228946
2026-01-16,302,5184513
2026-01-16,301,3869432
2026-01-16,202,2259124
2026-01-16,201,4902534
2026-02-16,602,5951387
2026-02-16,601,3027432
2026-02-16,502,2043330
2026-02-16,501,2700401
2026-02-16,402,1196485
2026-02-16,401,3232949
2026-02-16,302,5213147
2026-02-16,301,3886481
2026-02-16,202,2268887
2026-02-16,201,4921609
2026-03-16,602,5962589
2026-03-16,601,3047411
2026-03-16,502,2049398
2026-03-16,501,2706764
2026-03-16,402,1200079
2026-03-16,401,3255156
2026-03-16,302,5245809
2026-03-16,301,3908560
2026-03-16,202,2279640
2026-03-16,201,4936981
2026-04-15,602,5973014
2026-04-15,601,3072821
2026-04-15,502,2062251
2026-04-15,501,2714814
2026-04-15,402,1204198
2026-04-15,401,3284483
2026-04-15,302,5274384
2026-04-15,301,3932004
2026-04-15,202,2292471
2026-04-15,201,4959450
2026-05-12,602,5989359
2026-05-12,601,3111281
2026-05-12,502,2079604
2026-05-12,501,2722261
2026-05-12,402,1209722
2026-05-12,401,3318043
2026-05-12,302,5314264
2026-05-12,301,3960434
2026-05-12,202,2304004
2026-05-12,201,4985942
2026-06-15,602,6014694
2026-06-15,601,3165743
2026-06-15,502,2103231
2026-06-15,501,2735295
2026-06-15,402,1220213
2026-06-15,401,3377273
2026-06-15,302,5373594
2026-06-15,301,4011137
2026-06-15,202,5373594
2026-06-15,201,4011577
2026-07-13,602,6044616
2026-07-13,601,3202472
2026-07-13,502,2135259
2026-07-13,501,2752707
2026-07-13,402,1229586
2026-07-13,401,3437253
2026-07-13,302,5433704
2026-07-13,301,4068952
2026-07-13,202,2340828
2026-07-13,201,5060042
2026-08-14,602,6073848
2026-08-14,601,3243065
2026-08-14,502,2159414
2026-08-14,501,2774686
2026-08-14,402,1239567
2026-08-14,401,3495073
2026-08-14,302,5483864
2026-08-14,301,4125559
2026-08-14,202,2364837
2026-08-14,201,5104021
2026-09-15,602,6103317
2026-09-15,601,3301663
2026-09-15,502,2188472
2026-09-15,501,2787466
2026-09-15,402,1249895
2026-09-15,401,3544953
2026-09-15,302,5533213
2026-09-15,301,4181143
2026-09-15,202,2385458
2026-09-15,201,5136480`;

type Aba = "painel" | "leituras" | "medidores" | "ciclos" | "config";

interface Medidor {
  id: number; unidade: string; tipo: string;
  numero_serie: string | null; modelo: string | null; fabricante: string | null;
  ano_fabricacao: number | null; casas_inteiras: number; casas_decimais: number;
  fator_leitura: number; ativo: boolean; observacao: string | null;
}
interface Leitura {
  id: number; medidor_id: number; unidade: string; data_leitura: string;
  leitura_m3: number; anotado: string | null; status: string; origem: string;
  leiturista: string | null; observacao: string | null;
}
interface Ciclo {
  id: number; mes_referencia: string; status: string;
  data_leitura_anterior: string | null; data_leitura_atual: string | null;
  consumo_medido_m3: number | null; consumo_corrigido_m3: number | null;
  valor_total: number | null; vencimento: string | null;
  valor_cobrado_administradora: number | null; observacao: string | null;
}
interface RateioLinha {
  unidade: string; consumo: number; percentual: number | null; valor: number; estimado: boolean;
  aprovado_por?: string | null;
}
interface RateioResp {
  ciclo: Ciclo; aprovado?: boolean; linhas: RateioLinha[];
  resumo?: Record<string, number | null>; alertas?: { nivel: string; texto: string }[];
}

// Padrão de casas decimais do módulo: m³ = 3, R$ = 2, R$/m³ = 4, % = 2.
const inp = "w-full text-sm rounded-md border border-slate-300 px-2.5 py-1.5";
const fmt3 = (v: number | null | undefined) => (v == null ? "—" : v.toLocaleString("pt-BR", { minimumFractionDigits: 3, maximumFractionDigits: 3 }));
const brl = (v: number | null | undefined) => (v == null ? "—" : v.toLocaleString("pt-BR", { style: "currency", currency: "BRL" }));
const pct = (v: number | null | undefined) => (v == null ? "—" : Number(v).toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 }));
const precoM3 = (v: number | null | undefined) => "R$ " + Number(v ?? 0).toLocaleString("pt-BR", { minimumFractionDigits: 4, maximumFractionDigits: 4 }) + "/m³";
function fmtData(iso: string | null): string {
  if (!iso) return "—";
  const [a, m, d] = iso.split("-");
  return a && m && d ? `${d}/${m}/${a}` : iso;
}

export function GasPage({ condominioId, onVoltar, mesReferencia, voltarLabel }: Props) {
  const qc = useQueryClient();
  const { podeEscrever, ehSindico } = usePermissoes();
  const podeEditar = podeEscrever(condominioId, "gas");
  const [enviarCalc, setEnviarCalc] = useState(false);
  // Quando chamado com um mês (ex.: vindo do alerta do Resumo de DOC's), abre
  // direto na aba Ciclos & Rateio.
  const [aba, setAba] = useState<Aba>(mesReferencia ? "ciclos" : "painel");

  const invalidar = () => {
    ["gas-painel", "gas-medidores", "gas-leituras", "gas-ciclos", "gas-rateio", "gas-config"].forEach(
      (k) => qc.invalidateQueries({ queryKey: [k, condominioId] }),
    );
  };

  const tabs: { id: Aba; label: string; icone: typeof Flame }[] = [
    { id: "painel", label: "Painel", icone: Flame },
    { id: "leituras", label: "Leituras", icone: Gauge },
    { id: "medidores", label: "Medidores", icone: Settings },
    { id: "ciclos", label: "Ciclos & Rateio", icone: CalendarRange },
    { id: "config", label: "Configuração", icone: Settings },
  ];

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 py-6">
      <button type="button" onClick={onVoltar}
        className={voltarLabel
          ? "inline-flex items-center gap-1.5 text-sm font-semibold text-rose-700 hover:text-rose-900 mb-2"
          : "inline-flex items-center gap-1.5 text-sm text-slate-600 hover:text-slate-900 mb-2"}>
        <ArrowLeft size={16} /> {voltarLabel ?? "Voltar"}
      </button>

      <div className="flex items-center gap-2.5 mb-4">
        <div className="w-9 h-9 rounded-lg bg-orange-50 text-orange-600 flex items-center justify-center">
          <Flame size={20} />
        </div>
        <div>
          <h2 className="text-lg font-semibold text-slate-900">Controle e Rateio de Gás</h2>
          <p className="text-xs text-slate-500">
            Leituras dos medidores, rateio por apartamento e auditoria da conta da concessionária.
          </p>
        </div>
        {ehSindico(condominioId) && (
          <button type="button" onClick={() => setEnviarCalc(true)}
            title="Envia por e-mail à administradora o link da Calculadora de Rateio de Gás (gratuita, no navegador)"
            className="ml-auto inline-flex items-center gap-1.5 text-sm px-3 py-2 rounded-md border border-orange-300 text-orange-700 bg-orange-50 hover:bg-orange-100">
            <Mail size={16} /> Enviar Calculadora à administradora
          </button>
        )}
      </div>
      {enviarCalc && <EnviarCalculadoraModal condominioId={condominioId} onFechar={() => setEnviarCalc(false)} />}

      <div className="flex flex-wrap gap-1.5 mb-5 border-b border-slate-200">
        {tabs.map((t) => (
          <button key={t.id} type="button" onClick={() => setAba(t.id)}
            className={`inline-flex items-center gap-1.5 text-sm px-3 py-2 -mb-px border-b-2 ${
              aba === t.id ? "border-orange-500 text-orange-700 font-medium" : "border-transparent text-slate-500 hover:text-slate-800"
            }`}>
            <t.icone size={15} /> {t.label}
          </button>
        ))}
      </div>

      {aba === "painel" && <Painel condominioId={condominioId} />}
      {aba === "leituras" && <Leituras condominioId={condominioId} podeEditar={podeEditar} onMudou={invalidar} />}
      {aba === "medidores" && <Medidores condominioId={condominioId} podeEditar={podeEditar} onMudou={invalidar} />}
      {aba === "ciclos" && <Ciclos condominioId={condominioId} podeEditar={podeEditar} onMudou={invalidar} mesDestaque={mesReferencia} />}
      {aba === "config" && <Config condominioId={condominioId} podeEditar={podeEditar} onMudou={invalidar} />}
    </div>
  );
}

// ============================ Painel ============================
function Painel({ condominioId }: { condominioId: number }) {
  const [aptoGrafico, setAptoGrafico] = useState<string | null>(null);
  const { data, isLoading } = useQuery<{ serie: any[]; metodo_rateio: string }>({
    queryKey: ["gas-painel", condominioId],
    queryFn: () => api.get(`/gas/condominios/${condominioId}/painel`),
  });
  const { data: mensal } = useQuery<ConsumoMensal>({
    queryKey: ["gas-consumo-mensal", condominioId],
    queryFn: () => api.get(`/gas/condominios/${condominioId}/consumo-mensal`),
  });
  if (isLoading) return <Carregando />;
  const serie = data?.serie ?? [];
  const temGrafico = mensal && mensal.periodos.length > 0;
  if (!serie.length && !temGrafico) return <Vazio texto="Ainda não há dados. Importe leituras e cadastre uma conta em “Ciclos & Rateio”." />;
  const maxV = Math.max(...serie.map((s) => Math.max(s.geral_m3, s.aptos_m3)), 1);
  // Gráfico: escolhe UM apto e mostra barras por mês (até 13 meses).
  const unidades = mensal?.unidades ?? [];
  const aptoSel = aptoGrafico && unidades.includes(aptoGrafico) ? aptoGrafico : unidades[0];
  const dadosGrafico = (mensal?.periodos ?? []).map((p) => ({
    mes: p.label,
    consumo: p.aptos?.[aptoSel!] ?? 0,
  }));
  return (
    <div>
      {serie.length > 0 && (
      <div className="rounded-lg border border-slate-200 bg-white p-4 mb-4">
        <h3 className="text-sm font-semibold text-slate-800 mb-3">Medidor geral × soma dos aptos × perda</h3>
        <div className="space-y-2">
          {serie.map((s) => (
            <div key={s.mes} className="flex items-center gap-3 text-xs">
              <span className="w-16 text-slate-600">{s.mes}</span>
              <div className="flex-1 h-5 bg-slate-100 rounded relative overflow-hidden">
                <div className="absolute inset-y-0 left-0 bg-orange-400/70" style={{ width: `${(s.geral_m3 / maxV) * 100}%` }} />
                <div className="absolute inset-y-0 left-0 bg-sky-500/70 h-2 top-1.5" style={{ width: `${(s.aptos_m3 / maxV) * 100}%` }} />
              </div>
              <span className="w-40 text-right text-slate-600">
                geral {fmt3(s.geral_m3)} · aptos {fmt3(s.aptos_m3)}
              </span>
              <span className={`w-24 text-right font-medium ${s.perda_pct > 10 ? "text-rose-600" : s.perda_pct >= 5 ? "text-amber-600" : "text-emerald-600"}`}>
                perda {pct(s.perda_pct)}%
              </span>
            </div>
          ))}
        </div>
        <p className="text-[11px] text-slate-400 mt-3">
          Barra laranja = medidor geral (concessionária) · linha azul = soma dos apartamentos. Perda = geral − aptos − pontos sem medidor.
        </p>
      </div>
      )}
      {temGrafico && (
        <div className="rounded-2xl border p-4 mb-4" style={GLASS_CHART_CARD}>
          <div className="flex items-center justify-between gap-3 mb-1 flex-wrap">
            <h3 className="text-sm font-semibold text-white">Consumo do apto {aptoSel} — últimos {dadosGrafico.length} meses</h3>
            <select
              value={aptoSel ?? ""}
              onChange={(e) => setAptoGrafico(e.target.value)}
              className="text-xs rounded-md border border-white/25 bg-white/10 text-white px-2 py-1"
            >
              {unidades.map((u) => (
                <option key={u} value={u} className="text-slate-800">Apto {u}</option>
              ))}
            </select>
          </div>
          <p className="text-[11px] mb-3" style={{ color: CHART_AXIS }}>Barras de consumo mês a mês (m³) do apartamento escolhido. Troque o apto no seletor ao lado.</p>
          <div style={{ width: "100%", height: 360 }}>
            <ResponsiveContainer>
              <BarChart data={dadosGrafico} margin={{ top: 5, right: 12, left: -8, bottom: 0 }}>
                <CartesianGrid stroke={CHART_GRID} vertical={false} />
                <XAxis dataKey="mes" tick={{ fill: CHART_AXIS, fontSize: 11 }} />
                <YAxis tick={{ fill: CHART_AXIS, fontSize: 11 }} width={44} />
                <Tooltip contentStyle={CHART_TOOLTIP} />
                <Bar dataKey="consumo" name="Consumo (m³)"
                  fill={PIE_COLORS_DARK[0]} radius={[3, 3, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}
    </div>
  );
}

// ============================ Medidores ============================
function Medidores({ condominioId, podeEditar, onMudou }: { condominioId: number; podeEditar: boolean; onMudou: () => void }) {
  const { data, isLoading } = useQuery<{ itens: Medidor[] }>({
    queryKey: ["gas-medidores", condominioId],
    queryFn: () => api.get(`/gas/condominios/${condominioId}/medidores`),
  });
  const [aberto, setAberto] = useState(false);
  const vazio = { unidade: "", tipo: "individual", numero_serie: "", modelo: "", fabricante: "", ano_fabricacao: "", casas_inteiras: "5", casas_decimais: "3" };
  const [form, setForm] = useState({ ...vazio });

  const criar = useMutation({
    mutationFn: () => api.post(`/gas/condominios/${condominioId}/medidores`, {
      unidade: form.unidade.trim(), tipo: form.tipo,
      numero_serie: form.numero_serie || null, modelo: form.modelo || null, fabricante: form.fabricante || null,
      ano_fabricacao: form.ano_fabricacao ? Number(form.ano_fabricacao) : null,
      casas_inteiras: Number(form.casas_inteiras || 5), casas_decimais: Number(form.casas_decimais || 3),
    }),
    onSuccess: () => { setForm({ ...vazio }); setAberto(false); onMudou(); },
  });

  if (isLoading) return <Carregando />;
  const itens = data?.itens ?? [];
  return (
    <div>
      <div className="flex justify-between items-center mb-3">
        <p className="text-xs text-slate-500">{itens.length} medidor(es). Cada relógio guarda seus dígitos (pretos + vermelhos).</p>
        {podeEditar && !aberto && (
          <button type="button" onClick={() => setAberto(true)}
            className="inline-flex items-center gap-1.5 text-sm px-3 py-2 rounded-md bg-brand-700 text-white hover:brightness-110">
            <Plus size={16} /> Novo medidor
          </button>
        )}
      </div>

      {podeEditar && aberto && (
        <div className="mb-5 rounded-lg border border-slate-200 bg-white p-4">
          <div className="flex justify-between items-center mb-3">
            <h3 className="text-sm font-semibold text-slate-800">Novo medidor</h3>
            <button onClick={() => setAberto(false)} className="text-slate-400 hover:text-slate-700"><X size={18} /></button>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            <div><label className="text-xs text-slate-500">Unidade * (ex.: 302 ou GERAL)</label>
              <input className={inp} value={form.unidade} onChange={(e) => setForm({ ...form, unidade: e.target.value })} /></div>
            <div><label className="text-xs text-slate-500">Tipo</label>
              <select className={inp} value={form.tipo} onChange={(e) => setForm({ ...form, tipo: e.target.value })}>
                <option value="individual">Individual (apto)</option>
                <option value="geral">Geral (concessionária)</option>
              </select></div>
            <div><label className="text-xs text-slate-500">Nº de série</label>
              <input className={inp} value={form.numero_serie} onChange={(e) => setForm({ ...form, numero_serie: e.target.value })} /></div>
            <div><label className="text-xs text-slate-500">Modelo</label>
              <input className={inp} placeholder="Daeflex G2.5" value={form.modelo} onChange={(e) => setForm({ ...form, modelo: e.target.value })} /></div>
            <div><label className="text-xs text-slate-500">Ano de fabricação</label>
              <input className={inp} inputMode="numeric" value={form.ano_fabricacao} onChange={(e) => setForm({ ...form, ano_fabricacao: e.target.value })} /></div>
            <div className="grid grid-cols-2 gap-2">
              <div><label className="text-xs text-slate-500">Díg. pretos</label>
                <input className={inp} inputMode="numeric" value={form.casas_inteiras} onChange={(e) => setForm({ ...form, casas_inteiras: e.target.value })} /></div>
              <div><label className="text-xs text-slate-500">Díg. vermelhos</label>
                <input className={inp} inputMode="numeric" value={form.casas_decimais} onChange={(e) => setForm({ ...form, casas_decimais: e.target.value })} /></div>
            </div>
          </div>
          <div className="mt-3 flex justify-end gap-2">
            <button onClick={() => setForm({ ...vazio })} className="text-sm px-3 py-1.5 text-slate-600">Limpar</button>
            <button disabled={!form.unidade.trim() || criar.isPending} onClick={() => criar.mutate()}
              className="inline-flex items-center gap-1.5 text-sm px-3 py-2 rounded-md bg-brand-700 text-white disabled:opacity-50">
              {criar.isPending ? <Loader2 size={16} className="animate-spin" /> : <Plus size={16} />} Salvar
            </button>
          </div>
          {criar.isError && <p className="text-xs text-rose-600 mt-2">{(criar.error as Error).message}</p>}
        </div>
      )}

      <div className="rounded-lg border border-slate-200 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-500 text-xs">
            <tr><th className="text-left px-3 py-2">Unidade</th><th className="text-left px-3 py-2">Tipo</th>
              <th className="text-left px-3 py-2">Modelo / Série</th><th className="text-left px-3 py-2">Dígitos</th></tr>
          </thead>
          <tbody>
            {itens.map((m) => (
              <tr key={m.id} className="border-t border-slate-100">
                <td className="px-3 py-2 font-medium">{m.unidade}</td>
                <td className="px-3 py-2 text-slate-600">{m.tipo === "geral" ? "Geral" : "Individual"}</td>
                <td className="px-3 py-2 text-slate-600">{[m.modelo, m.numero_serie].filter(Boolean).join(" · ") || "—"}</td>
                <td className="px-3 py-2 text-slate-600">{m.casas_inteiras} + {m.casas_decimais} <span className="text-slate-400">(÷{m.fator_leitura})</span></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ============================ Leituras ============================
function Leituras({ condominioId, podeEditar, onMudou }: { condominioId: number; podeEditar: boolean; onMudou: () => void }) {
  const { data: meds } = useQuery<{ itens: Medidor[] }>({
    queryKey: ["gas-medidores", condominioId],
    queryFn: () => api.get(`/gas/condominios/${condominioId}/medidores`),
  });
  const { data, isLoading } = useQuery<{ itens: Leitura[] }>({
    queryKey: ["gas-leituras", condominioId],
    queryFn: () => api.get(`/gas/condominios/${condominioId}/leituras`),
  });
  const [medidorId, setMedidorId] = useState<string>("");
  const [dataLeitura, setDataLeitura] = useState(new Date().toISOString().slice(0, 10));
  const [anotado, setAnotado] = useState("");
  const [importAberto, setImportAberto] = useState(false);
  const [csv, setCsv] = useState("");

  const lancar = useMutation({
    mutationFn: () => api.post(`/gas/condominios/${condominioId}/leituras`, {
      medidor_id: Number(medidorId), data_leitura: dataLeitura, anotado: anotado.trim(),
    }),
    onSuccess: () => { setAnotado(""); onMudou(); },
  });
  const importar = useMutation<{ criadas: number; puladas: number; suspeitas: number }>({
    mutationFn: () => api.post(`/gas/condominios/${condominioId}/importar-historico`, { csv_texto: csv }),
    onSuccess: () => { setCsv(""); setImportAberto(false); onMudou(); },
  });
  const { data: mensal } = useQuery<ConsumoMensal>({
    queryKey: ["gas-consumo-mensal", condominioId],
    queryFn: () => api.get(`/gas/condominios/${condominioId}/consumo-mensal`),
  });

  const itens = data?.itens ?? [];
  return (
    <div>
      {podeEditar && (
        <div className="mb-4 rounded-lg border border-slate-200 bg-white p-4">
          <h3 className="text-sm font-semibold text-slate-800 mb-3">Lançar leitura</h3>
          <div className="grid grid-cols-1 sm:grid-cols-4 gap-3 items-end">
            <div><label className="text-xs text-slate-500">Medidor</label>
              <select className={inp} value={medidorId} onChange={(e) => setMedidorId(e.target.value)}>
                <option value="">— selecionar —</option>
                {meds?.itens.map((m) => <option key={m.id} value={m.id}>{m.unidade}{m.tipo === "geral" ? " (geral)" : ""}</option>)}
              </select></div>
            <div><label className="text-xs text-slate-500">Data</label>
              <input type="date" className={inp} value={dataLeitura} onChange={(e) => setDataLeitura(e.target.value)} /></div>
            <div><label className="text-xs text-slate-500">Número anotado (como no relógio)</label>
              <input className={inp} inputMode="numeric" placeholder="5213147" value={anotado} onChange={(e) => setAnotado(e.target.value)} /></div>
            <button disabled={!medidorId || !anotado.trim() || lancar.isPending} onClick={() => lancar.mutate()}
              className="inline-flex items-center justify-center gap-1.5 text-sm px-3 py-2 rounded-md bg-brand-700 text-white disabled:opacity-50">
              {lancar.isPending ? <Loader2 size={16} className="animate-spin" /> : <Plus size={16} />} Lançar
            </button>
          </div>
          {lancar.isError && <p className="text-xs text-rose-600 mt-2">{(lancar.error as Error).message}</p>}
          {lancar.isSuccess && <p className="text-xs text-emerald-600 mt-2">Leitura registrada.</p>}

          <div className="mt-3">
            <button onClick={() => setImportAberto((v) => !v)} className="inline-flex items-center gap-1.5 text-xs text-brand-700 hover:underline">
              <Upload size={14} /> Importar histórico (colar tabela data,unidade,anotado)
            </button>
            {importAberto && (
              <div className="mt-2">
                <textarea className={`${inp} font-mono text-xs`} rows={6} placeholder={"2026-01-16,302,5184513\n2026-01-16,301,3869432"} value={csv} onChange={(e) => setCsv(e.target.value)} />
                <p className="text-[11px] text-slate-400 mt-1">
                  Uma linha por leitura: <b>data,unidade,número anotado</b> (ex.: 2026-01-16,302,5184513). Os medidores são criados sozinhos na 1ª importação.
                </p>
                <div className="flex justify-between items-center mt-2">
                  <button type="button" onClick={() => setCsv(SEED_GOLDEN)}
                    className="text-xs text-brand-700 hover:underline">
                    Preencher com o histórico do Golden (jan–set/2026)
                  </button>
                  <button disabled={!csv.trim() || importar.isPending} onClick={() => importar.mutate()}
                    className="inline-flex items-center gap-1.5 text-sm px-3 py-2 rounded-md bg-brand-700 text-white disabled:opacity-50">
                    {importar.isPending ? <Loader2 size={16} className="animate-spin" /> : <Upload size={16} />} Importar
                  </button>
                </div>
                {importar.isSuccess && (
                  <p className="text-xs text-emerald-600 mt-1">
                    {importar.data.criadas} leitura(s) importada(s), {importar.data.suspeitas} suspeita(s), {importar.data.puladas} já existiam.
                  </p>
                )}
                {importar.isError && <p className="text-xs text-rose-600 mt-1">{(importar.error as Error).message}</p>}
              </div>
            )}
          </div>
        </div>
      )}

      {mensal && mensal.periodos.length > 0 && (
        <div className="mb-4 rounded-lg border border-slate-200 overflow-hidden">
          <div className="px-3 py-2 bg-slate-50 text-sm font-semibold text-slate-700">Totais por mês</div>
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-500 text-xs">
              <tr><th className="text-left px-3 py-2">Mês</th>
                <th className="text-right px-3 py-2">Total aptos (m³)</th>
                <th className="text-right px-3 py-2">Total operadora (m³)</th>
                <th className="text-right px-3 py-2">Perda (m³)</th></tr>
            </thead>
            <tbody>
              {mensal.periodos.map((p) => {
                const perda = p.operadora != null ? p.operadora - p.total_aptos : null;
                return (
                  <tr key={p.fim} className="border-t border-slate-100">
                    <td className="px-3 py-2">{p.label} <span className="text-[10px] text-slate-400">({fmtData(p.ini)}→{fmtData(p.fim)})</span></td>
                    <td className="px-3 py-2 text-right tabular-nums">{fmt3(p.total_aptos)}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{p.operadora != null ? fmt3(p.operadora) : "—"}</td>
                    <td className={`px-3 py-2 text-right tabular-nums ${perda != null && perda < 0 ? "text-rose-600" : "text-slate-500"}`}>{perda != null ? fmt3(perda) : "—"}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          <p className="text-[11px] text-slate-400 px-3 py-2">Total operadora = consumo corrigido do ciclo daquele mês (quando a conta já foi lançada).</p>
        </div>
      )}

      {isLoading ? <Carregando /> : itens.length === 0 ? <Vazio texto="Nenhuma leitura ainda." /> : (
        <div className="rounded-lg border border-slate-200 overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-500 text-xs">
              <tr><th className="text-left px-3 py-2">Data</th><th className="text-left px-3 py-2">Unidade</th>
                <th className="text-right px-3 py-2">Leitura (m³)</th><th className="text-left px-3 py-2">Origem</th>
                <th className="text-left px-3 py-2">Status</th></tr>
            </thead>
            <tbody>
              {itens.map((l) => (
                <tr key={l.id} className={`border-t border-slate-100 ${l.status === "suspeita" ? "bg-rose-50" : ""}`}>
                  <td className="px-3 py-2">{fmtData(l.data_leitura)}</td>
                  <td className="px-3 py-2 font-medium">{l.unidade}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{fmt3(l.leitura_m3)}</td>
                  <td className="px-3 py-2 text-slate-500 text-xs">{l.origem === "importacao_historica" ? "histórico" : "app"}</td>
                  <td className="px-3 py-2">
                    {l.status === "suspeita"
                      ? <span className="inline-flex items-center gap-1 text-xs text-rose-700"><AlertTriangle size={13} /> suspeita{l.observacao ? ` — ${l.observacao.replace("Importação: ", "")}` : ""}</span>
                      : <span className="text-xs text-emerald-600">ok</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ============================ Ciclos & Rateio ============================
function Ciclos({ condominioId, podeEditar, onMudou, mesDestaque }: { condominioId: number; podeEditar: boolean; onMudou: () => void; mesDestaque?: string }) {
  const { data, isLoading } = useQuery<{ itens: Ciclo[] }>({
    queryKey: ["gas-ciclos", condominioId],
    queryFn: () => api.get(`/gas/condominios/${condominioId}/ciclos`),
  });
  const [aberto, setAberto] = useState(false);
  const [selecionado, setSelecionado] = useState<number | null>(null);

  // Quando chega com um mês em destaque (vindo do alerta do Resumo de DOC's),
  // abre automaticamente o rateio daquele ciclo.
  useEffect(() => {
    if (!mesDestaque || !data?.itens) return;
    const alvo = data.itens.find((c) => c.mes_referencia === mesDestaque);
    if (alvo) setSelecionado(alvo.id);
  }, [mesDestaque, data]);
  const vazio = { mes_referencia: "", data_leitura_anterior: "", data_leitura_atual: "", leitura_geral_anterior: "", leitura_geral_atual: "", consumo_medido_m3: "", consumo_corrigido_m3: "", valor_total: "", valor_cobrado_administradora: "", vencimento: "" };
  const [form, setForm] = useState({ ...vazio });

  // Aceita os DOIS formatos: "3.001,31" (digitado à mão, BR) e "3001.31"/"363.28"
  // (preenchido pela IA/leitura do PDF, ponto decimal). Regra: se tem vírgula, ela é
  // o decimal e os pontos são milhar; se NÃO tem vírgula, o ponto é o decimal.
  const num = (s: string) => {
    const t = (s || "").trim();
    if (!t) return null;
    const norm = t.includes(",") ? t.replace(/\./g, "").replace(",", ".") : t;
    const n = Number(norm);
    return Number.isFinite(n) ? n : null;
  };
  // Consumo medido = leitura atual − anterior do medidor geral (quando informadas).
  const gAnt = num(form.leitura_geral_anterior);
  const gAtu = num(form.leitura_geral_atual);
  const medidoCalc = gAnt != null && gAtu != null && gAtu >= gAnt ? gAtu - gAnt : null;
  const medidoEfetivo = num(form.consumo_medido_m3) ?? medidoCalc;
  const corrig = num(form.consumo_corrigido_m3);
  const fator = medidoEfetivo && corrig ? corrig / medidoEfetivo : null;
  const criar = useMutation({
    mutationFn: () => api.post(`/gas/condominios/${condominioId}/ciclos`, {
      mes_referencia: form.mes_referencia.trim(),
      data_leitura_anterior: form.data_leitura_anterior || null, data_leitura_atual: form.data_leitura_atual || null,
      leitura_geral_anterior: num(form.leitura_geral_anterior), leitura_geral_atual: num(form.leitura_geral_atual),
      consumo_medido_m3: num(form.consumo_medido_m3), consumo_corrigido_m3: num(form.consumo_corrigido_m3),
      valor_total: num(form.valor_total), valor_cobrado_administradora: num(form.valor_cobrado_administradora),
      vencimento: form.vencimento || null,
    }),
    onSuccess: () => { setForm({ ...vazio }); setAberto(false); onMudou(); },
  });

  const fileRef = useRef<HTMLInputElement>(null);
  const [aviso, setAviso] = useState<string | null>(null);
  const [pdfEscolhido, setPdfEscolhido] = useState<File | null>(null);
  const aplicarCampos = (c: Record<string, any>) => {
    setAviso(c.aviso || null);
    setForm((f) => ({
      ...f,
      mes_referencia: c.mes_referencia || f.mes_referencia,
      data_leitura_anterior: c.data_leitura_anterior || f.data_leitura_anterior,
      data_leitura_atual: c.data_leitura_atual || f.data_leitura_atual,
      leitura_geral_anterior: c.leitura_geral_anterior != null ? String(c.leitura_geral_anterior) : f.leitura_geral_anterior,
      leitura_geral_atual: c.leitura_geral_atual != null ? String(c.leitura_geral_atual) : f.leitura_geral_atual,
      consumo_medido_m3: c.consumo_medido_m3 != null ? String(c.consumo_medido_m3) : f.consumo_medido_m3,
      consumo_corrigido_m3: c.consumo_corrigido_m3 != null ? String(c.consumo_corrigido_m3) : f.consumo_corrigido_m3,
      valor_total: c.valor_total != null ? String(c.valor_total) : f.valor_total,
      vencimento: c.vencimento || f.vencimento,
    }));
  };
  const analisar = useMutation<Record<string, any>, Error, File>({
    mutationFn: (file: File) => api.upload(`/gas/condominios/${condominioId}/analisar-fatura`, file, "arquivo"),
    onSuccess: aplicarCampos,
  });
  const analisarIa = useMutation<Record<string, any>, Error, File>({
    mutationFn: (file: File) => api.upload(`/gas/condominios/${condominioId}/analisar-fatura-ia`, file, "arquivo"),
    onSuccess: aplicarCampos,
  });
  const excluir = useMutation<void, Error, number>({
    mutationFn: (id) => api.delete(`/gas/condominios/${condominioId}/ciclos/${id}`),
    onSuccess: () => { setSelecionado(null); onMudou(); },
  });

  if (isLoading) return <Carregando />;
  const itens = data?.itens ?? [];
  return (
    <div>
      <div className="flex justify-between items-center mb-3">
        <p className="text-xs text-slate-500">Uma linha por conta mensal da concessionária.</p>
        {podeEditar && !aberto && (
          <button type="button" onClick={() => setAberto(true)}
            className="inline-flex items-center gap-1.5 text-sm px-3 py-2 rounded-md bg-brand-700 text-white hover:brightness-110">
            <Plus size={16} /> Nova conta (ciclo)
          </button>
        )}
      </div>

      {podeEditar && aberto && (
        <div className="mb-5 rounded-lg border border-slate-200 bg-white p-4">
          <div className="flex justify-between items-center mb-3">
            <h3 className="text-sm font-semibold text-slate-800">Nova conta da concessionária</h3>
            <button onClick={() => setAberto(false)} className="text-slate-400 hover:text-slate-700"><X size={18} /></button>
          </div>
          {/* Leitura automática do PDF avulso da Sulgás (baixado do site) */}
          <div className="mb-3 p-2.5 rounded-md bg-orange-50 border border-orange-100">
            <input ref={fileRef} type="file" accept="application/pdf" className="hidden"
              onChange={(e) => { const f = e.target.files?.[0]; if (f) { setPdfEscolhido(f); analisar.mutate(f); } e.target.value = ""; }} />
            <div className="flex flex-wrap gap-2">
              <button type="button" onClick={() => fileRef.current?.click()} disabled={analisar.isPending || analisarIa.isPending}
                className="inline-flex items-center gap-1.5 text-sm px-3 py-2 rounded-md bg-orange-600 text-white disabled:opacity-50">
                {analisar.isPending ? <Loader2 size={16} className="animate-spin" /> : <FileText size={16} />} Ler PDF da Sulgás e preencher
              </button>
              {pdfEscolhido && (
                <button type="button" onClick={() => analisarIa.mutate(pdfEscolhido)} disabled={analisar.isPending || analisarIa.isPending}
                  className="inline-flex items-center gap-1.5 text-sm px-3 py-2 rounded-md border border-orange-400 text-orange-700 hover:bg-orange-100 disabled:opacity-50">
                  {analisarIa.isPending ? <Loader2 size={16} className="animate-spin" /> : <FileText size={16} />} Ler com IA (melhor, com custo)
                </button>
              )}
            </div>
            <p className="text-[11px] text-slate-500 mt-1">
              Baixe a fatura direto do site da Sulgás e selecione aqui — o app preenche os campos abaixo (você confere e ajusta). O PDF fica anexado ao ciclo.
              Se algum campo faltar (fatura em imagem), use <b>“Ler com IA”</b> — mais preciso, <b>custo baixo</b> (lê só as 2 páginas da conta).
              O campo <b>“Cobrado pela administradora”</b> não vem na conta da Sulgás: preencha à mão.
            </p>
            {aviso && <p className="text-[11px] text-amber-700 mt-1">⚠ {aviso}</p>}
            {(analisar.isSuccess || analisarIa.isSuccess) && !aviso && <p className="text-[11px] text-emerald-600 mt-1">Campos preenchidos a partir do PDF. Confira antes de salvar.</p>}
            {analisar.isError && <p className="text-[11px] text-rose-600 mt-1">{(analisar.error as Error).message}</p>}
            {analisarIa.isError && <p className="text-[11px] text-rose-600 mt-1">{(analisarIa.error as Error).message}</p>}
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            <div><label className="text-xs text-slate-500">Mês de referência * (AAAA-MM)</label>
              <input className={inp} placeholder="2026-06" value={form.mes_referencia} onChange={(e) => setForm({ ...form, mes_referencia: e.target.value })} /></div>
            <div><label className="text-xs text-slate-500">Data leitura anterior</label>
              <input type="date" className={inp} value={form.data_leitura_anterior} onChange={(e) => setForm({ ...form, data_leitura_anterior: e.target.value })} /></div>
            <div><label className="text-xs text-slate-500">Data leitura atual</label>
              <input type="date" className={inp} value={form.data_leitura_atual} onChange={(e) => setForm({ ...form, data_leitura_atual: e.target.value })} /></div>
            <div><label className="text-xs text-slate-500">Leitura anterior do medidor geral</label>
              <input className={inp} inputMode="decimal" placeholder="opcional" value={form.leitura_geral_anterior} onChange={(e) => setForm({ ...form, leitura_geral_anterior: e.target.value })} /></div>
            <div><label className="text-xs text-slate-500">Leitura atual do medidor geral</label>
              <input className={inp} inputMode="decimal" placeholder="opcional" value={form.leitura_geral_atual} onChange={(e) => setForm({ ...form, leitura_geral_atual: e.target.value })} /></div>
            <div><label className="text-xs text-slate-500">Consumo medido (m³)</label>
              <input className={inp} inputMode="decimal" placeholder={medidoCalc != null ? `auto: ${fmt3(medidoCalc)}` : "do “Detalhamento do consumo”"} value={form.consumo_medido_m3} onChange={(e) => setForm({ ...form, consumo_medido_m3: e.target.value })} />
              {medidoCalc != null && !form.consumo_medido_m3.trim() && <p className="text-[11px] text-emerald-600 mt-0.5">Calculado do medidor geral: {fmt3(medidoCalc)} m³</p>}</div>
            <div><label className="text-xs text-slate-500">Consumo corrigido/faturado (m³)</label>
              <input className={inp} inputMode="decimal" placeholder="382,28" value={form.consumo_corrigido_m3} onChange={(e) => setForm({ ...form, consumo_corrigido_m3: e.target.value })} />
              {fator != null && <p className="text-[11px] text-slate-500 mt-0.5">Fator de correção: {fator.toFixed(4)}</p>}</div>
            <div><label className="text-xs text-slate-500">Valor total (R$)</label>
              <input className={inp} inputMode="decimal" placeholder="3001,31" value={form.valor_total} onChange={(e) => setForm({ ...form, valor_total: e.target.value })} /></div>
            <div><label className="text-xs text-slate-500">Cobrado pela administradora (R$)</label>
              <input className={inp} inputMode="decimal" placeholder="preencha à mão" value={form.valor_cobrado_administradora} onChange={(e) => setForm({ ...form, valor_cobrado_administradora: e.target.value })} />
              <p className="text-[11px] text-slate-400 mt-0.5">Total que a administradora rateou/cobrou dos aptos. Não vem na conta da Sulgás.</p></div>
            <div><label className="text-xs text-slate-500">Vencimento</label>
              <input type="date" className={inp} value={form.vencimento} onChange={(e) => setForm({ ...form, vencimento: e.target.value })} /></div>
          </div>
          <div className="mt-3 flex justify-end">
            <button disabled={!form.mes_referencia.trim() || criar.isPending} onClick={() => criar.mutate()}
              className="inline-flex items-center gap-1.5 text-sm px-3 py-2 rounded-md bg-brand-700 text-white disabled:opacity-50">
              {criar.isPending ? <Loader2 size={16} className="animate-spin" /> : <Plus size={16} />} Salvar conta
            </button>
          </div>
          {criar.isError && <p className="text-xs text-rose-600 mt-2">{(criar.error as Error).message}</p>}
        </div>
      )}

      <div className="rounded-lg border border-slate-200 overflow-hidden mb-4">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-500 text-xs">
            <tr><th className="text-left px-3 py-2">Mês</th><th className="text-right px-3 py-2">Valor</th>
              <th className="text-left px-3 py-2">Status</th><th className="px-3 py-2"></th></tr>
          </thead>
          <tbody>
            {itens.map((c) => (
              <tr key={c.id} className={`border-t border-slate-100 ${c.mes_referencia === mesDestaque ? "bg-amber-50" : ""}`}>
                <td className="px-3 py-2 font-medium">{c.mes_referencia}</td>
                <td className="px-3 py-2 text-right">{brl(c.valor_total)}</td>
                <td className="px-3 py-2">{c.status === "aprovado"
                  ? <span className="inline-flex items-center gap-1 text-xs text-emerald-700"><CheckCircle2 size={13} /> aprovado</span>
                  : <span className="text-xs text-amber-600">aberto</span>}</td>
                <td className="px-3 py-2 text-right">
                  <button onClick={() => setSelecionado(selecionado === c.id ? null : c.id)}
                    className="text-xs text-brand-700 hover:underline">{selecionado === c.id ? "fechar" : "ver rateio"}</button>
                  {podeEditar && (
                    <button onClick={() => { if (window.confirm(`Excluir DEFINITIVAMENTE a conta e o rateio de ${c.mes_referencia}?${c.status === "aprovado" ? " Este ciclo está APROVADO/congelado." : ""}\nNão dá para desfazer.`)) excluir.mutate(c.id); }}
                      className="text-xs text-rose-600 hover:underline ml-3">excluir</button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {selecionado && <Rateio condominioId={condominioId} cicloId={selecionado} podeEditar={podeEditar} onMudou={onMudou} />}
    </div>
  );
}

function Rateio({ condominioId, cicloId, podeEditar, onMudou }: { condominioId: number; cicloId: number; podeEditar: boolean; onMudou: () => void }) {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery<RateioResp>({
    queryKey: ["gas-rateio", condominioId, cicloId],
    queryFn: () => api.get(`/gas/condominios/${condominioId}/ciclos/${cicloId}/rateio`),
  });
  const aprovar = useMutation({
    mutationFn: () => api.post(`/gas/condominios/${condominioId}/ciclos/${cicloId}/aprovar`),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["gas-rateio", condominioId, cicloId] }); onMudou(); },
  });
  const reabrir = useMutation({
    mutationFn: () => api.post(`/gas/condominios/${condominioId}/ciclos/${cicloId}/reabrir`),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["gas-rateio", condominioId, cicloId] }); onMudou(); },
  });
  const [salvo, setSalvo] = useState<string | null>(null);
  const relatorio = useMutation<void, Error, "xlsx" | "pdf">({
    // O endpoint devolve o ARQUIVO (xlsx/pdf), não JSON — baixa como blob
    // (evita o erro "Unexpected token 'P', PK..." de tentar parsear o zip do xlsx).
    mutationFn: (formato) => baixarArquivoPost(
      `/gas/condominios/${condominioId}/ciclos/${cicloId}/relatorio?formato=${formato}`,
      {},
      formato === "xlsx" ? `rateio-gas-${cicloId}.xlsx` : `auditoria-gas-${cicloId}.pdf`,
    ),
    onSuccess: () => setSalvo("Arquivo gerado (verifique os downloads)."),
  });
  const leiturasCalc = useMutation<void, Error, void>({
    mutationFn: () => baixarArquivoPost(
      `/gas/condominios/${condominioId}/ciclos/${cicloId}/leituras-calculadora`,
      {}, `leituras-calculadora-${cicloId}.xlsx`),
    onSuccess: () => setSalvo("Arquivo de leituras gerado (verifique os downloads)."),
  });

  if (isLoading) return <Carregando />;
  if (!data) return null;
  const r = data.resumo ?? {};
  const aprovado = !!data.aprovado || data.ciclo?.status === "aprovado";
  return (
    <div className="rounded-lg border border-slate-200 bg-white p-4">
      <div className="flex flex-wrap justify-between items-center gap-2 mb-3">
        <h3 className="text-sm font-semibold text-slate-800">Rateio — {data.ciclo?.mes_referencia}</h3>
        <div className="flex items-center gap-2">
          <button disabled={relatorio.isPending} onClick={() => relatorio.mutate("xlsx")}
            className="inline-flex items-center gap-1.5 text-xs px-2.5 py-1.5 rounded-md border border-slate-300 text-slate-700 hover:bg-slate-50 disabled:opacity-50">
            <FileSpreadsheet size={14} /> Excel p/ administradora
          </button>
          <button disabled={relatorio.isPending} onClick={() => relatorio.mutate("pdf")}
            className="inline-flex items-center gap-1.5 text-xs px-2.5 py-1.5 rounded-md border border-slate-300 text-slate-700 hover:bg-slate-50 disabled:opacity-50">
            <Download size={14} /> PDF auditoria
          </button>
          <button disabled={leiturasCalc.isPending} onClick={() => leiturasCalc.mutate()}
            title="Excel de leituras no formato da Calculadora de Gás (para enviar à administradora)"
            className="inline-flex items-center gap-1.5 text-xs px-2.5 py-1.5 rounded-md border border-slate-300 text-slate-700 hover:bg-slate-50 disabled:opacity-50">
            <FileSpreadsheet size={14} /> Leituras p/ Calculadora
          </button>
          <a href={`/calculadora-gas/${r.metodo ? `?metodo=${String(r.metodo).charAt(0)}` : ""}`}
            target="_blank" rel="noopener noreferrer"
            title="Calculadora de Rateio de Gás (grátis, no navegador) — o link que se envia à administradora"
            className="text-xs text-sky-700 hover:underline">
            Abrir a Calculadora ↗
          </a>
          {podeEditar && (aprovado
            ? <button onClick={() => reabrir.mutate()} className="text-xs text-slate-600 hover:underline">Reabrir</button>
            : <button disabled={aprovar.isPending} onClick={() => aprovar.mutate()}
                className="inline-flex items-center gap-1.5 text-sm px-3 py-2 rounded-md bg-emerald-600 text-white disabled:opacity-50">
                {aprovar.isPending ? <Loader2 size={16} className="animate-spin" /> : <CheckCircle2 size={16} />} Aprovar (congela)
              </button>)}
        </div>
      </div>
      {salvo && <p className="text-xs text-emerald-600 mb-2">{salvo}</p>}
      {relatorio.isError && <p className="text-xs text-rose-600 mb-2">{(relatorio.error as Error).message}</p>}
      {aprovar.isError && <p className="text-xs text-rose-600 mb-2">{(aprovar.error as Error).message}</p>}

      {!aprovado && data.resumo && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-3 text-xs">
          <Kpi rotulo="Soma aptos" valor={`${fmt3(r.soma_individual_m3 as number)} m³`} />
          <Kpi rotulo="Consumo faturado (base)" valor={`${fmt3(r.consumo_medido_geral_m3 as number)} m³`} />
          <Kpi rotulo="Perda" valor={`${fmt3(r.perda_m3 as number)} m³ (${pct(r.perda_pct as number)}%)`} destaque={(r.perda_pct as number) > 10 ? "rose" : (r.perda_pct as number) >= 5 ? "amber" : "emerald"} />
          <Kpi rotulo="Preço efetivo (Tarifa)" valor={precoM3(r.preco_efetivo_por_m3 as number)} />
          <Kpi rotulo="Total aptos" valor={brl(r.valor_aptos as number)} />
          <Kpi rotulo="Fica p/ condomínio" valor={brl(r.valor_condominio as number)} />
          {r.diferenca != null && <Kpi rotulo="Dif. administradora" valor={`${brl(r.diferenca as number)} (${pct(r.diferenca_pct as number)}%)`} destaque={Math.abs(r.diferenca_pct as number) > 5 ? "rose" : "emerald"} />}
        </div>
      )}

      {data.alertas && data.alertas.length > 0 && (
        <div className="mb-3 space-y-1">
          {data.alertas.map((a, i) => (
            <p key={i} className={`text-xs inline-flex items-center gap-1 ${a.nivel === "critico" ? "text-rose-700" : a.nivel === "atencao" ? "text-amber-700" : "text-slate-600"}`}>
              <AlertTriangle size={13} /> {a.texto}
            </p>
          ))}
        </div>
      )}

      <div className="rounded-lg border border-slate-200 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-500 text-xs">
            <tr><th className="text-left px-3 py-2">Apto</th><th className="text-right px-3 py-2">Consumo (m³)</th>
              <th className="text-right px-3 py-2">%</th><th className="text-right px-3 py-2">Valor</th></tr>
          </thead>
          <tbody>
            {data.linhas.map((l) => (
              <tr key={l.unidade} className="border-t border-slate-100">
                <td className="px-3 py-2 font-medium">{l.unidade}{l.estimado && <span className="ml-1 text-[10px] text-amber-600">(estimado)</span>}</td>
                <td className="px-3 py-2 text-right tabular-nums">{fmt3(l.consumo)}</td>
                <td className="px-3 py-2 text-right text-slate-500">{l.percentual != null ? pct(l.percentual * 100) + "%" : "—"}</td>
                <td className="px-3 py-2 text-right tabular-nums">{brl(l.valor)}</td>
              </tr>
            ))}
          </tbody>
          {(() => {
            const somaCons = data.linhas.reduce((s, l) => s + (l.consumo || 0), 0);
            const somaVal = data.linhas.reduce((s, l) => s + (l.valor || 0), 0);
            const somaPct = data.linhas.reduce((s, l) => s + (l.percentual || 0), 0);
            return (
              <tfoot>
                <tr className="border-t-2 border-slate-300 bg-slate-50 font-semibold">
                  <td className="px-3 py-2">TOTAL ({data.linhas.length} aptos)</td>
                  <td className="px-3 py-2 text-right tabular-nums">{fmt3(somaCons)}</td>
                  <td className="px-3 py-2 text-right text-slate-500">{pct(somaPct * 100)}%</td>
                  <td className="px-3 py-2 text-right tabular-nums">{brl(somaVal)}</td>
                </tr>
              </tfoot>
            );
          })()}
        </table>
      </div>

      {/* Batimentos (bate no olhar se não fecha) */}
      {data.resumo && (() => {
        const somaCons = data.linhas.reduce((s, l) => s + (l.consumo || 0), 0);
        const somaVal = data.linhas.reduce((s, l) => s + (l.valor || 0), 0);
        const base = (r.consumo_medido_geral_m3 as number) ?? 0;
        const total = (r.valor_total as number) ?? 0;
        const difCons = somaCons - base;
        const difVal = somaVal - total;
        const okCons = Math.abs(difCons) < 0.5;
        const okVal = Math.abs(difVal) < 0.05;
        const linha = (ok: boolean, txt: string) => (
          <p className={`text-xs inline-flex items-center gap-1 ${ok ? "text-emerald-700" : "text-rose-700"}`}>
            {ok ? <CheckCircle2 size={13} /> : <AlertTriangle size={13} />} {txt}
          </p>
        );
        return (
          <div className="mt-3 space-y-1 rounded-md border border-slate-200 bg-slate-50 p-2.5">
            <p className="text-[11px] font-semibold text-slate-500 uppercase tracking-wide">Batimentos</p>
            {linha(okCons, `Consumo: soma dos aptos ${fmt3(somaCons)} m³ ${okCons ? "=" : "≠"} faturado ${fmt3(base)} m³` + (okCons ? "" : ` (diferença ${fmt3(difCons)} m³)`))}
            {linha(okVal, `Valor: soma dos aptos ${brl(somaVal)} + condomínio ${brl(r.valor_condominio as number)} ${okVal ? "=" : "≠"} conta ${brl(total)}`)}
            {r.diferenca != null && linha(Math.abs(r.diferenca_pct as number) <= 5,
              `Administradora: calculado ${brl(somaVal)} vs cobrado ${brl((somaVal) - (r.diferenca as number))} (diferença ${brl(r.diferenca as number)} / ${pct(r.diferenca_pct as number)}%)`)}
          </div>
        );
      })()}
      {aprovado && <p className="text-[11px] text-emerald-600 mt-2">Rateio congelado{data.linhas[0]?.aprovado_por ? ` por ${data.linhas[0].aprovado_por}` : ""}.</p>}
    </div>
  );
}

// ============================ Config ============================
function Config({ condominioId, podeEditar, onMudou }: { condominioId: number; podeEditar: boolean; onMudou: () => void }) {
  const { data, isLoading } = useQuery<{ gas_metodo_rateio: string; metodos: string[] }>({
    queryKey: ["gas-config", condominioId],
    queryFn: () => api.get(`/gas/condominios/${condominioId}/config`),
  });
  const [metodo, setMetodo] = useState<string>("");
  const salvar = useMutation({
    mutationFn: (m: string) => api.put(`/gas/condominios/${condominioId}/config`, { gas_metodo_rateio: m }),
    onSuccess: () => onMudou(),
  });
  if (isLoading) return <Carregando />;
  const atual = metodo || data?.gas_metodo_rateio || "B_preco_efetivo";
  return (
    <div className="rounded-lg border border-slate-200 bg-white p-4 max-w-xl">
      <h3 className="text-sm font-semibold text-slate-800 mb-1">Método de rateio</h3>
      <p className="text-xs text-slate-500 mb-3">Decisão de assembleia. Muda como o valor da conta é dividido entre os apartamentos.</p>
      <label className="flex items-start gap-2 p-2 rounded hover:bg-slate-50 cursor-pointer">
        <input type="radio" name="metodo" checked={atual === "B_preco_efetivo"} disabled={!podeEditar}
          onChange={() => setMetodo("B_preco_efetivo")} className="mt-1" />
        <span className="text-sm"><b>B — Preço efetivo</b><br />
          <span className="text-xs text-slate-500">Cada apto paga pelo seu consumo (preço por m³). A perda/diferença fica separada, geralmente para o condomínio, e aparece explícita.</span></span>
      </label>
      <label className="flex items-start gap-2 p-2 rounded hover:bg-slate-50 cursor-pointer">
        <input type="radio" name="metodo" checked={atual === "A_conta_integral"} disabled={!podeEditar}
          onChange={() => setMetodo("A_conta_integral")} className="mt-1" />
        <span className="text-sm"><b>A — Conta integral</b><br />
          <span className="text-xs text-slate-500">A soma dos aptos fecha exatamente com a conta; a perda é diluída entre os apartamentos.</span></span>
      </label>
      {podeEditar && (
        <div className="mt-3 flex items-center gap-2">
          <button disabled={salvar.isPending} onClick={() => salvar.mutate(atual)}
            className="inline-flex items-center gap-1.5 text-sm px-3 py-2 rounded-md bg-brand-700 text-white disabled:opacity-50">
            {salvar.isPending ? <Loader2 size={16} className="animate-spin" /> : <CheckCircle2 size={16} />} Salvar
          </button>
          {salvar.isSuccess && <span className="text-xs text-emerald-600">Salvo.</span>}
        </div>
      )}
    </div>
  );
}

// ============================ auxiliares ============================
function Carregando() {
  return <div className="flex items-center gap-2 text-sm text-slate-500 py-8 justify-center"><Loader2 size={16} className="animate-spin" /> Carregando…</div>;
}
function Vazio({ texto }: { texto: string }) {
  return <div className="text-sm text-slate-500 py-8 text-center">{texto}</div>;
}
function Kpi({ rotulo, valor, destaque }: { rotulo: string; valor: string; destaque?: "rose" | "amber" | "emerald" }) {
  const cor = destaque === "rose" ? "text-rose-700" : destaque === "amber" ? "text-amber-700" : destaque === "emerald" ? "text-emerald-700" : "text-slate-800";
  return (
    <div className="rounded-md border border-slate-200 px-2.5 py-1.5">
      <div className="text-[11px] text-slate-500">{rotulo}</div>
      <div className={`text-sm font-medium ${cor}`}>{valor}</div>
    </div>
  );
}


// =====================================================================
// Enviar o link da Calculadora de Gás (web) à administradora — 0.1.76
// O link é montado pelo SERVIDOR; aqui só se escolhem os e-mails e a mensagem.
// =====================================================================
interface InfoCalculadora { link: string; metodo_rateio?: string; emails_administradora: string[]; email_disponivel: boolean }

export function EnviarCalculadoraModal({ condominioId, onFechar }: { condominioId: number; onFechar: () => void }) {
  const { data: info, isLoading } = useQuery<InfoCalculadora>({
    queryKey: ["gas-calculadora", condominioId],
    queryFn: () => api.get(`/gas/condominios/${condominioId}/calculadora`),
  });
  const [emails, setEmails] = useState<string | null>(null);
  const [mensagem, setMensagem] = useState("");
  const [lembrar, setLembrar] = useState(true);
  const [copiado, setCopiado] = useState(false);
  const qc = useQueryClient();
  const valorEmails = emails ?? (info?.emails_administradora ?? []).join("; ");
  const enviar = useMutation({
    mutationFn: () => api.post<{ enviados: string[]; falhas: string[] }>(
      `/gas/condominios/${condominioId}/calculadora/enviar`, { emails: valorEmails, mensagem, lembrar }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["gas-calculadora", condominioId] }),
  });
  const copiar = async () => {
    if (!info) return;
    try {
      await navigator.clipboard.writeText(info.link);
      setCopiado(true);
      setTimeout(() => setCopiado(false), 2500);
    } catch {
      window.prompt("Copie o link:", info.link);
    }
  };
  const res = enviar.data;
  return (
    <div className="fixed inset-0 z-50 bg-slate-900/40 flex items-center justify-center p-4" role="dialog" aria-modal="true"
      onClick={(e) => { if (e.target === e.currentTarget) onFechar(); }}>
      <div className="bg-white rounded-xl shadow-xl w-full max-w-lg p-5">
        <div className="flex items-start justify-between gap-3 mb-3">
          <div>
            <h3 className="text-base font-semibold text-slate-900 flex items-center gap-2">
              <Mail size={18} className="text-orange-600" /> Enviar a Calculadora de Gás
            </h3>
            <p className="text-xs text-slate-500 mt-0.5">
              A administradora recebe o link da calculadora gratuita (sem instalar nada) e um passo a passo.
              Se ela responder, a resposta vem para o seu e-mail.
            </p>
          </div>
          <button type="button" onClick={onFechar} className="text-slate-400 hover:text-slate-700" aria-label="Fechar"><X size={18} /></button>
        </div>

        {isLoading || !info ? (
          <div className="flex items-center gap-2 text-sm text-slate-500 py-6 justify-center">
            <Loader2 size={16} className="animate-spin" /> Carregando…
          </div>
        ) : res ? (
          <div className="space-y-2">
            {res.enviados.length > 0 && (
              <div className="rounded-md bg-emerald-50 border border-emerald-200 text-emerald-800 text-sm p-3 flex gap-2">
                <CheckCircle2 size={16} className="mt-0.5 shrink-0" /> Enviado para {res.enviados.join(", ")}.
              </div>
            )}
            {res.falhas.length > 0 && (
              <div className="rounded-md bg-rose-50 border border-rose-200 text-rose-800 text-sm p-3 flex gap-2">
                <AlertTriangle size={16} className="mt-0.5 shrink-0" />
                Não foi possível enviar para {res.falhas.join(", ")}. Confira o endereço e tente de novo.
              </div>
            )}
            <div className="flex justify-end pt-2">
              <button type="button" onClick={onFechar} className="text-sm px-3 py-2 rounded-md bg-slate-800 text-white">Fechar</button>
            </div>
          </div>
        ) : (
          <div className="space-y-3">
            <div className="rounded-md bg-slate-50 border border-slate-200 p-2.5 flex items-center gap-2">
              <span className="text-xs text-slate-600 truncate flex-1">{info.link}</span>
              <button type="button" onClick={copiar}
                className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded border border-slate-300 hover:bg-white">
                <Copy size={13} /> {copiado ? "Copiado!" : "Copiar link"}
              </button>
            </div>
            {info.metodo_rateio && (
              <p className="text-xs text-slate-600">
                Método de rateio deste condomínio:{" "}
                <b>{info.metodo_rateio.startsWith("A") ? "A — Conta integral" : "B — Preço efetivo"}</b>.
                A calculadora já abre com ele marcado (dá para mudar em Configuração).
              </p>
            )}
            {!info.email_disponivel && (
              <div className="rounded-md bg-amber-50 border border-amber-200 text-amber-800 text-xs p-2.5">
                O envio de e-mail não está ligado no servidor. Use "Copiar link" e mande por WhatsApp ou pelo seu e-mail.
              </div>
            )}
            <label className="block">
              <span className="text-xs font-medium text-slate-600">E-mail(s) da administradora</span>
              <input value={valorEmails} onChange={(e) => setEmails(e.target.value)} disabled={!info.email_disponivel}
                placeholder="ex.: financeiro@administradora.com.br; contas@administradora.com.br"
                className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm" />
              <span className="text-[11px] text-slate-400">Até 5, separados por ponto e vírgula.</span>
            </label>
            <label className="block">
              <span className="text-xs font-medium text-slate-600">Mensagem (opcional)</span>
              <textarea value={mensagem} onChange={(e) => setMensagem(e.target.value)} rows={3} maxLength={1000}
                disabled={!info.email_disponivel}
                placeholder="ex.: Segue a calculadora para o rateio do gás deste mês. As leituras vão em seguida."
                className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm" />
            </label>
            <label className="flex items-center gap-2 text-xs text-slate-600">
              <input type="checkbox" checked={lembrar} onChange={(e) => setLembrar(e.target.checked)} />
              Lembrar estes e-mails para os próximos envios deste condomínio
            </label>
            {enviar.isError && (
              <div className="rounded-md bg-rose-50 border border-rose-200 text-rose-800 text-xs p-2.5">
                {(enviar.error as Error)?.message || "Não foi possível enviar."}
              </div>
            )}
            <div className="flex justify-end gap-2 pt-1">
              <button type="button" onClick={onFechar} className="text-sm px-3 py-2 rounded-md border border-slate-300 text-slate-700">Cancelar</button>
              <button type="button" disabled={!info.email_disponivel || !valorEmails.trim() || enviar.isPending}
                onClick={() => enviar.mutate()}
                className="inline-flex items-center gap-1.5 text-sm px-3 py-2 rounded-md bg-orange-600 text-white disabled:opacity-50">
                {enviar.isPending ? <Loader2 size={15} className="animate-spin" /> : <Send size={15} />} Enviar
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
